import * as THREE from 'three'

import { classifyParts } from './hullDetect.js'
import { collectVariants, parseVariantName, switchableParts } from './stateVariants.js'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'

import { AIRFRAME_MODEL, COCKPIT } from '../config/constants.js'
import { autoFitAircraft, sampleInFrame } from './autoFit.js'
import { findCockpits, seatEyeY } from './cockpitProbe.js'
import { DECK_SPOTS, PARKED_RADIUS_FT, clearanceFt, isUsableSpot } from '../world/deckSpots.js'
import { DEG, FT_TO_M } from '../util/units.js'

/**
 * Optional glTF model overrides.
 *
 * Procedural geometry has a ceiling, and for a cockpit that ceiling is well
 * below what a real scanned F-14 pit looks like. Boxes and toruses can express
 * layout and proportion; they cannot express rivets, worn paint, stitched
 * leather, chipped placards or the hundred small specular breaks that make the
 * eye accept a surface as manufactured. No amount of rearranging primitives
 * closes that gap.
 *
 * So there is a slot. Drop a .glb into public/models/, name it in
 * constants.js, and it replaces the procedural version. Nothing else in the
 * simulator cares: the physics, the grading and the camera all key off the
 * design eye, which stays where it is.
 *
 * Loading is deliberately non-fatal. A missing or broken model logs a warning
 * and leaves the procedural cockpit in place, because a trainer that refuses
 * to start because an optional asset is absent is worse than one that looks
 * plainer than you hoped.
 */
export class ModelOverrides {
  constructor() {
    this.loader = new GLTFLoader()
    this.cockpit = null
    this.airframe = null
    this.notes = []
  }

  /**
   * @param {THREE.Object3D} parent  the aircraft group, so the cockpit
   *        inherits the aircraft's position and attitude for free
   * @returns {Promise<{cockpitLoaded: boolean, notes: string[]}>}
   */
  async load(parent) {
    const [cockpitLoaded, airframeLoaded] = await Promise.all([
      this._loadOne(parent, COCKPIT, 'cockpit', { interior: true }),
      this._loadOne(parent, AIRFRAME_MODEL, 'airframe', { interior: false }),
    ])
    return { cockpitLoaded, airframeLoaded, notes: this.notes }
  }

  /**
   * @param {THREE.Object3D} parent
   * @param {object} cfg   one of COCKPIT / AIRFRAME_MODEL
   * @param {'cockpit'|'airframe'} slot
   */
  async _loadOne(parent, cfg, slot, { interior }) {
    if (!cfg?.gltfUrl) {
      this.notes.push(`no glTF ${slot} configured — using the procedural one`)
      return false
    }

    try {
      const gltf = await this.loader.loadAsync(cfg.gltfUrl)
      const model = gltf.scene

      if (slot === 'airframe' && cfg.autoFit) {
        const fit = autoFitAircraft(model, {
          trueLengthM: cfg.trueLengthM ?? 19.1,
          verticalCentreY: cfg.verticalCentreY ?? 0.25,
          // Place the model so the FIXED design eye lands in the cockpit.
          eyeZ: cfg.eyeZ ?? -7.05,
          eyeFractionFromNose: cfg.eyeFractionFromNose ?? 0.28,
          noseZ: cfg.noseZ,
        })
        this.fit = fit
        if (fit.ok) {
          const [nx, ny, nz] = cfg.nudge ?? [0, 0, 0]
          model.position.x += nx
          model.position.y += ny
          model.position.z += nz
          this.notes.push(
            `airframe auto-fitted: fuselage on the ${fit.lengthAxis.toUpperCase()} axis, ` +
              `yawed ${fit.yawDeg} deg to face -Z, scaled x${fit.scale.toFixed(4)}; ` +
              `span comes out ${fit.spanAfter.toFixed(1)} m ` +
              `(a real Tomcat is 19.6 m wings-forward, 11.7 m swept); ` +
              `design eye lands ${((fit.eyeFraction ?? 0) * 100).toFixed(0)}% back from the nose`,
          )
        } else {
          this.notes.push(`airframe auto-fit failed (${fit.reason}) — using the explicit transform`)
        }
      } else {
        model.scale.setScalar(cfg.gltfScale ?? 1)
        model.position.set(...(cfg.gltfOffset ?? [0, 0, 0]))
        const [ry, rx, rz] = cfg.gltfRotationDeg ?? [0, 0, 0]
        model.rotation.set(
          (rx * Math.PI) / 180,
          (ry * Math.PI) / 180,
          (rz * Math.PI) / 180,
        )
      }

      // Which meshes are the hull, and which are fittings inside it. Measured
      // once, before the traverse, because the answer depends on the parts
      // relative to each other. See hullDetect.js.
      const meshes = []
      model.traverse((o) => {
        if (o.isMesh) meshes.push(o)
      })
      const boxes = meshes.map((o) => {
        o.geometry.computeBoundingBox()
        const b = o.geometry.boundingBox
        return { name: o.name, size: { x: b.max.x - b.min.x, y: b.max.y - b.min.y, z: b.max.z - b.min.z } }
      })
      const hull = new Set(
        classifyParts(boxes)
          .map((p, i) => (p.isHull ? meshes[i] : null))
          .filter(Boolean),
      )

      model.traverse((o) => {
        if (!o.isMesh) return
        // Interior geometry must not self-shadow: at cockpit scale the shadow
        // map has nowhere near the resolution and the result is acne over the
        // whole panel.
        o.castShadow = !interior

        // The airframe casts onto the deck and the sea but does not RECEIVE.
        //
        // That is not laziness about self-shadowing, it is the cockpit. The
        // camera sits inside this mesh, and the sun's shadow map covers a
        // 680 m frustum in 2048 texels — a third of a metre each. Cockpit
        // detail is centimetres, so every interior surface lands inside one
        // shadow texel and the whole pit turns into a field of acne. Losing
        // wing-on-fuselage self-shadowing outside is a far smaller price.
        o.receiveShadow = false
        if (interior) o.frustumCulled = false

        // Exterior skins get single-sided rendering. Downloaded models are very
        // often authored doubleSided because it hides holes in the mesh; the
        // price is that any camera INSIDE the hull — which is precisely where
        // the pilot sits — sees the inner face of the fuselage instead of the
        // world, and the screen goes black. Culling backfaces makes the hull
        // transparent from within, which is the behaviour every flight sim
        // relies on.
        // ONLY THE HULL. Forcing every material on the airframe to FrontSide
        // was too broad: this model's cockpit is a separate 5,098 triangle mesh
        // authored double-sided because it is a single layer of surfaces rather
        // than a solid, and single-siding it deleted every triangle facing away
        // from the pilot. Sitting still you never see it, because the surfaces
        // you are looking at are the ones facing you. Turn your head and pieces
        // of your own aeroplane wink out.
        //
        // Fittings inside the hull are left exactly as the author built them —
        // there is nothing you need to see through in a cockpit tub.
        if (!interior && hull.has(o)) {
          for (const m of Array.isArray(o.material) ? o.material : [o.material]) {
            if (m) m.side = THREE.FrontSide
          }
        }
        // Anything that is not hull is close to the camera and small, so it is
        // never worth culling and popping is very visible.
        if (!hull.has(o)) o.frustumCulled = false
      })

      // Read the model's own on/off variants before it goes into the scene, so
      // the very first frame draws the right one.
      if (slot === 'airframe') this._findStateVariants(model)

      parent.add(model)
      this[slot] = model
      this.notes.push(`glTF ${slot} loaded from ${cfg.gltfUrl}`)
      this._reportFit(model, slot, cfg)
      if (slot === 'airframe') this._reportCockpits(model, parent, cfg)
      return true
    } catch (err) {
      const message = `glTF ${slot} "${cfg.gltfUrl}" failed to load (${
        err?.message ?? err
      }) — keeping the procedural one`
      console.warn(`[models] ${message}`)
      this.notes.push(message)
      return false
    }
  }

  /**
   * Measure the loaded model and say, in plain numbers, how to line it up.
   *
   * A downloaded model arrives in whatever units and orientation its author
   * used — centimetres, Z-up, nose facing +X, origin at a wingtip. Aligning it
   * by editing a number, reloading and squinting is miserable. This does the
   * arithmetic once and prints the scale and offset to paste back into
   * constants.js, plus a warning if the model is facing the wrong way, which
   * is the failure that makes the aircraft fly sideways rather than merely
   * look wrong.
   */
  /**
   * Find the parts of the model that exist in more than one state.
   *
   * The author of this model did the work already, and it is worth reading the
   * node names rather than assuming they did not: the gear is modelled TWICE,
   * as `f14a-gearOn` — the legs, the wheels and the open doors, ten thousand
   * triangles of it — and `f14a-gearOff`, a hundred and eighty-seven triangles
   * of closed bay doors. Both were being drawn, so the Tomcat ran up the
   * starboard side at 350 knots with its wheels hanging out.
   *
   * The convention is `<part>On` / `<part>Off`, so this reads it generically
   * rather than hard-coding one aeroplane's node names. A model that does not
   * use the convention yields nothing and nothing changes.
   */
  _findStateVariants(model) {
    const nodes = []
    model.traverse((o) => nodes.push(o))
    this.variants = collectVariants(nodes)
    const found = switchableParts(this.variants)
    if (found.length) {
      this.notes.push(`glTF carries on/off variants for: ${found.join(', ')}`)
      console.info(`[models] on/off variants found: ${found.join(', ')}`)
    }
  }

  /**
   * Show the variant that matches what the aeroplane is actually doing.
   *
   * Binary, because the model has no in-between: there is a gear-up mesh and a
   * gear-down mesh and nothing between them. Swapping at the halfway point of
   * the transit is the least-wrong moment — the gear is visibly on its way
   * either side of it, and the handle has been down for a second by then.
   */
  /**
   * Force one part of a cloned model into a state, by name.
   *
   * `Object3D.clone()` copies `visible` as it stands, so a jet cloned onto the
   * deck at a moment when the flying aeroplane had its gear up is a parked
   * Tomcat resting on nothing. Parked aircraft sit on their wheels, always,
   * whatever the one you are flying happens to be doing.
   */
  _forceVariant(root, part, on) {
    root.traverse((o) => {
      const v = parseVariantName(o.name)
      if (!v || v.part !== part) return
      o.visible = v.state === (on ? 'on' : 'off')
    })
  }

  applyState(s) {
    if (!this.variants || !s) return
    const show = (part, on) => {
      const v = this.variants[part]
      if (!v?.on || !v?.off) return
      v.on.visible = on
      v.off.visible = !on
    }
    show('gear', (s.gearNorm ?? 0) > 0.5)
    // The refuelling probe. This model ships only the retracted half, so this
    // does nothing — which is the right amount of work for an aeroplane that
    // never tanks in a CASE 1 pattern.
    show('refuel', false)
  }

  _reportFit(model, slot, cfg) {
    const box = new THREE.Box3().setFromObject(model)
    const size = box.getSize(new THREE.Vector3())
    const centre = box.getCenter(new THREE.Vector3())

    console.info(
      `[models] ${slot} bounding box: ` +
        `${size.x.toFixed(2)} wide x ${size.y.toFixed(2)} tall x ${size.z.toFixed(2)} long (metres), ` +
        `centred at (${centre.x.toFixed(2)}, ${centre.y.toFixed(2)}, ${centre.z.toFixed(2)})`,
    )

    if (slot !== 'airframe') return

    // Real F-14B: 19.1 m long, 19.55 m span with the wings forward.
    const TRUE_LENGTH_M = 19.1
    const TRUE_SPAN_M = 19.55

    if (size.z > size.x * 1.15) {
      const suggested = ((cfg.gltfScale ?? 1) * TRUE_LENGTH_M) / size.z
      console.info(
        `[models] airframe looks nose-along-Z (correct). ` +
          `For a true 19.1 m length set gltfScale to about ${suggested.toFixed(4)}.`,
      )
    } else if (size.x > size.z * 1.15) {
      console.warn(
        `[models] airframe is WIDER than it is long, so its nose probably points along X, not -Z. ` +
          `The physics builds its quaternion assuming nose = -Z, so this will fly sideways. ` +
          `Set AIRFRAME_MODEL.gltfRotationDeg to [90, 0, 0] or [-90, 0, 0] to correct it. ` +
          `For a true 19.55 m span set gltfScale to about ` +
          `${(((cfg.gltfScale ?? 1) * TRUE_SPAN_M) / size.x).toFixed(4)}.`,
      )
    }

    // Where does the pilot's eye land inside this model? On a Tomcat the front
    // seat is about 23% of the way back from the nose — NOT the 28% a side
    // view suggests, which is the bulkhead between the two cockpits. The probe
    // below reports the measured answer; this is the sanity check.
    const EYE_Z = -7.05
    const frac = (EYE_Z - box.min.z) / Math.max(size.z, 1e-6)
    const verdict =
      frac > 0.16 && frac < 0.32
        ? 'looks right'
        : 'SUSPECT — see the cockpit probe below for the measured figure'
    console.info(
      `[models] design eye sits ${(frac * 100).toFixed(0)}% back from the nose — ${verdict}`,
    )

    if (centre.length() > 3) {
      console.info(
        `[models] airframe origin is ${centre.length().toFixed(1)} m off its own centre. ` +
          `To centre it, set gltfOffset to ` +
          `[${(-centre.x).toFixed(2)}, ${(-centre.y).toFixed(2)}, ${(-centre.z).toFixed(2)}].`,
      )
    }
  }

  /**
   * Find the cockpits and say where they are.
   *
   * This is the diagnostic that would have saved a long afternoon. A Tomcat
   * has two of them, and putting the design eye at the "obvious" 28% of the
   * length lands it on the bulkhead between the pilot and the RIO — inside
   * solid structure, which from the camera looks exactly like a broken model
   * rather than a misplaced camera.
   *
   * Swapping in a different aeroplane means reading two numbers off the
   * console instead of nudging constants and reloading.
   */
  _reportCockpits(model, parent, cfg) {
    try {
      const samples = sampleInFrame(model, parent)
      let noseZ = Infinity
      let tailZ = -Infinity
      for (let i = 2; i < samples.length; i += 3) {
        if (samples[i] < noseZ) noseZ = samples[i]
        if (samples[i] > tailZ) tailZ = samples[i]
      }
      const lengthM = tailZ - noseZ
      const found = findCockpits(samples, { noseZ, lengthM })
      if (!found.ok) {
        console.info('[models] no cockpit cavity found — the model may be exterior only')
        return
      }
      for (const [i, seat] of found.seats.entries()) {
        console.info(
          `[models] cockpit ${i + 1}: ${seat.aftOfNoseM.toFixed(2)} m aft of the nose ` +
            `(${(seat.fraction * 100).toFixed(1)}% of length), floor ${seat.floorY.toFixed(2)}, ` +
            `coaming ${seat.coamingY.toFixed(2)}, canopy ${seat.canopyY.toFixed(2)} m`,
        )
      }
      const front = found.seats[0]
      const wantY = seatEyeY(front)
      const eyeY = 1.02
      console.info(
        `[models] to sit in the front cockpit: AIRFRAME_MODEL.eyeFractionFromNose ` +
          `${front.fraction.toFixed(3)} (now ${cfg.eyeFractionFromNose}), ` +
          `COCKPIT.seatOffsetM [0, ${(wantY - eyeY).toFixed(2)}, 0]`,
      )
    } catch (err) {
      console.warn('[models] cockpit probe failed', err)
    }
  }

  /**
   * Park the rest of the air wing.
   *
   * Clones of the same model, which costs almost nothing — three.js clones
   * share geometry and materials, so nine parked Tomcats are nine more draw
   * calls rather than nine more meshes' worth of memory.
   *
   * The spots come from src/world/deckSpots.js and every one of them is
   * checked against the landing area before it is used. That is not belt and
   * braces: two of the six-pack positions were originally FOUL — parked five
   * and twenty-three feet inside the strip the aeroplane lands on — and from
   * any camera angle in this simulator they looked completely fine.
   *
   * @param {THREE.Object3D} deckGroup the carrier's own group, so the parked
   *        aircraft pitch, roll and heave with the ship
   */
  parkOnDeck(deckGroup) {
    if (!this.airframe) return 0

    this.parked = []
    let placed = 0

    // How far the model hangs below its own origin.
    //
    // Taken from the auto-fit, which measured it while the model was still
    // unparented. Box3.setFromObject returns a WORLD-space box, and by the
    // time this runs the airframe is a child of the flying aeroplane — eight
    // hundred feet up at the initial. Measuring it here put every parked jet
    // 240 metres below the deck, and the console cheerfully reported nine
    // aircraft parked and clear of the landing area.
    const dropM = this.fit?.box ? -this.fit.box.min.y : 2.47

    for (const spot of DECK_SPOTS) {
      if (!isUsableSpot(spot)) {
        const clearance = clearanceFt(spot.x, spot.y, PARKED_RADIUS_FT)
        console.warn(
          `[deck] refusing to park at "${spot.name}" — ` +
            (clearance < 0
              ? `${Math.abs(clearance).toFixed(0)} ft inside the landing area, which is a foul deck`
              : 'its wheels are not on the flight deck'),
        )
        continue
      }

      // A HOLDER, not the clone itself. The auto-fit lives in the model root's
      // own position, rotation and scale; writing a parking position straight
      // onto a clone overwrites the fit and the aeroplane arrives unscaled, at
      // the wrong height and facing whichever way its author left it.
      const holder = new THREE.Group()
      holder.name = `parked ${spot.name}`
      const clone = this.airframe.clone(true)
      // Nine more aeroplanes in the sun's 2048-texel shadow map, every frame
      // the carrier is in frustum — which is the whole groove and the trap.
      // They are on a flat deck in the open; nothing they cast is worth that.
      clone.traverse((o) => {
        if (o.isMesh) o.castShadow = false
      })
      // Wheels down, always. `clone()` copies `visible` as it stands, so a jet
      // cloned while the flying aeroplane happened to have its gear up is a
      // parked Tomcat resting on its belly — and the deck park is placed once,
      // at load, so it would stay that way for the whole session.
      this._forceVariant(clone, 'gear', true)
      holder.add(clone)

      // Carrier group: +X starboard, +Y up, -Z forward. The spot list is in
      // the ship's own frame, where +X is forward and +Y is starboard.
      holder.position.set(spot.y * FT_TO_M, dropM, -spot.x * FT_TO_M)
      // Positive rotation about +Y swings the nose to port, and a spot heading
      // is measured positive to starboard.
      holder.rotation.y = -spot.headingDeg * DEG

      deckGroup.add(holder)
      this.parked.push(holder)
      placed++
    }

    const tightest = Math.min(
      ...DECK_SPOTS.map((sp) => clearanceFt(sp.x, sp.y, PARKED_RADIUS_FT)).filter((c) => c < 400),
    )
    console.info(
      `[deck] ${placed} aircraft parked, wheels on deck and clear of the landing area ` +
        `(tightest ${tightest.toFixed(0)} ft)`,
    )
    return placed
  }

  dispose() {
    for (const model of [this.cockpit, this.airframe]) this._disposeModel(model)
  }

  /**
   * Parked aircraft are CLONES, and three.js clones share geometry and
   * materials with the original. Disposing them would free the buffers the
   * player's own aeroplane is still drawing from, so they are only detached.
   */
  unparkAll() {
    for (const holder of this.parked ?? []) holder.parent?.remove(holder)
    this.parked = []
  }

  _disposeModel(root) {
    root?.traverse((o) => {
      if (o.geometry) o.geometry.dispose()
      if (o.material) {
        const mats = Array.isArray(o.material) ? o.material : [o.material]
        for (const m of mats) {
          for (const key of Object.keys(m)) {
            const v = m[key]
            if (v && v.isTexture) v.dispose()
          }
          m.dispose()
        }
      }
    })
  }
}
